/**
 * MessageList container component that displays a scrollable list of chat messages.
 * Handles auto-scroll behavior, empty states, and scroll position tracking.
 */
import type { ChatMessage } from "@nucleus/shared-types";
import { ArrowDown } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { Message } from "./Message";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/** Threshold in pixels from bottom to consider "at bottom" */
const SCROLL_THRESHOLD = 100;

interface MessageListProps {
	/** Array of messages to display */
	messages: ChatMessage[];
	/** Custom className for the container */
	className?: string;
}

/**
 * MessageList component displays messages in chronological order with auto-scroll behavior.
 *
 * Features:
 * - Auto-scrolls to bottom when new messages arrive (unless user has scrolled up)
 * - Shows "scroll to bottom" button when not at bottom
 * - Displays empty state placeholder when no messages
 * @param props - Component props
 * @param props.messages - Array of message objects to display
 * @param props.className - Optional additional CSS classes
 */
export function MessageList({ messages, className }: MessageListProps) {
	const scrollContainerRef = useRef<HTMLDivElement>(null);
	const [isAtBottom, setIsAtBottom] = useState(true);
	const [userHasScrolled, setUserHasScrolled] = useState(false);

	/**
	 * Checks if the scroll container is at the bottom.
	 * @returns True if scrolled to within SCROLL_THRESHOLD of bottom
	 */
	const checkIfAtBottom = (): boolean => {
		const container = scrollContainerRef.current;
		if (!container) return true;

		const { scrollTop, scrollHeight, clientHeight } = container;
		const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

		return distanceFromBottom <= SCROLL_THRESHOLD;
	};

	/**
	 * Scrolls to the bottom of the message list.
	 * @param behavior - Scroll behavior ("smooth" or "auto")
	 */
	const scrollToBottom = (behavior: ScrollBehavior = "smooth") => {
		const container = scrollContainerRef.current;
		if (!container) return;

		container.scrollTo({
			top: container.scrollHeight,
			behavior,
		});

		setIsAtBottom(true);
		setUserHasScrolled(false);
	};

	/**
	 * Handles scroll events to detect user scroll position.
	 */
	const handleScroll = () => {
		const atBottom = checkIfAtBottom();
		setIsAtBottom(atBottom);

		// If user scrolls up, mark that they've manually scrolled
		if (!atBottom) {
			setUserHasScrolled(true);
		}
	};

	/**
	 * Auto-scroll to bottom when new messages arrive (if user hasn't scrolled up).
	 */
	useEffect(() => {
		if (messages.length === 0) return;

		// Auto-scroll if user hasn't manually scrolled up
		if (!userHasScrolled) {
			// Use instant scroll for first message, smooth for subsequent
			const behavior = messages.length === 1 ? "auto" : "smooth";
			scrollToBottom(behavior);
		}
	}, [messages, userHasScrolled]);

	// Empty state
	if (messages.length === 0) {
		return (
			<div
				className={cn(
					"flex h-full flex-col items-center justify-center p-6 text-center",
					className
				)}
			>
				<div className="rounded-lg border border-dashed p-8">
					<h2 className="text-lg font-medium text-muted-foreground">
						No messages yet
					</h2>
					<p className="mt-2 text-sm text-muted-foreground">
						Start a conversation with the AI agent
					</p>
				</div>
			</div>
		);
	}

	return (
		<div className={cn("relative h-full", className)}>
			{/* Scrollable message container */}
			<div
				ref={scrollContainerRef}
				onScroll={handleScroll}
				className="h-full overflow-y-auto px-4 py-6"
			>
				{/* Message list */}
				<div className="mx-auto max-w-3xl space-y-4">
					{messages.map((message, index) => {
						// Determine if this is the first message in a group
						// A group is consecutive messages from the same role
						const prevMessage = index > 0 ? messages[index - 1] : null;
						const isFirstInGroup = prevMessage?.role !== message.role;

						return (
							<div key={message.id} className={cn(!isFirstInGroup && "mt-1")}>
								<Message message={message} isFirstInGroup={isFirstInGroup} />
							</div>
						);
					})}
				</div>
			</div>

			{/* Scroll to bottom button - shows when not at bottom */}
			{!isAtBottom && (
				<div className="absolute bottom-4 left-1/2 -translate-x-1/2">
					<Button
						size="sm"
						variant="secondary"
						className="shadow-lg"
						onClick={() => { scrollToBottom("smooth"); }}
						aria-label="Scroll to bottom"
					>
						<ArrowDown className="mr-2 h-4 w-4" />
						Scroll to bottom
					</Button>
				</div>
			)}
		</div>
	);
}
